/**
 * Utility function to validate E-way bill address requirements
 * @param {Object} partyBilling - Billing address data
 * @param {Object} partyDelivery - Delivery address data
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validateEwaybillAddresses = (partyBilling, partyDelivery, type) => {
  const requiredFields = [
    { key: 'line1', label: 'Address Details' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'countrycode', label: 'Country' },
    { key: 'statecode', label: 'State' },
    { key: 'district', label: 'District' },
    { key: 'city', label: 'City' },
  ];

  const foreignReqFields = [
    { key: 'line1', label: 'Address' },
    { key: 'countrycode', label: 'Country' },
    { key: 'city', label: 'City' },
  ];

  const checkFields = partyBilling?.countrycode ? requiredFields : foreignReqFields;

  const deliveryOnlyFields = [{ key: 'consigneename', label: 'Company Name' }];

  const billingMissingFields = [];
  const deliveryMissingFields = [];

  /* =========================
     HELPER CHECK FUNCTION
  ========================= */

  const isStateValid = (obj) => {
    if (obj?.statecode && obj?.statecode !== '0') return true;
    if (obj?.foreignstate) return true;
    return false;
  };

  const isDistrictValid = (obj) => {
    if (obj?.district && obj?.district !== '0') return true;
    if (obj?.foreigndistrict) return true;
    return false;
  };

  /* =========================
     BILLING VALIDATION
  ========================= */

  checkFields.forEach((field) => {
    if (field.key === 'statecode') {
      if (!isStateValid(partyBilling)) {
        billingMissingFields.push(field.label);
      }
    } else if (field.key === 'district') {
      if (!isDistrictValid(partyBilling)) {
        billingMissingFields.push(field.label);
      }
    } else {
      if (!partyBilling?.[field.key]) {
        billingMissingFields.push(field.label);
      }
    }
  });

  /* =========================
     DELIVERY VALIDATION
  ========================= */

  requiredFields.forEach((field) => {
    if (field.key === 'statecode') {
      if (!isStateValid(partyDelivery)) {
        deliveryMissingFields.push(field.label);
      }
    } else if (field.key === 'district') {
      if (!isDistrictValid(partyDelivery)) {
        deliveryMissingFields.push(field.label);
      }
    } else {
      if (!partyDelivery?.[field.key]) {
        deliveryMissingFields.push(field.label);
      }
    }
  });

  deliveryOnlyFields.forEach((field) => {
    if (!partyDelivery?.[field.key]) {
      deliveryMissingFields.push(field.label);
    }
  });

  const hasBillingIssues = billingMissingFields.length > 0;
  const hasDeliveryIssues = deliveryMissingFields.length > 0;

  let isValid = true;
  let errorMessage = '';

  if (type && hasBillingIssues) {
    isValid = false;
    const billingFields = billingMissingFields.join(', ');
    errorMessage = `Please fill ${billingFields} of Billing address to proceed with E-invoice creation`;
  }

  if (!type && hasBillingIssues && hasDeliveryIssues) {
    isValid = false;
    errorMessage = `Please fill ${billingMissingFields.join(', ')} of Billing address and ${deliveryMissingFields.join(', ')} of Shipping address to proceed with E-Way Bill creation`;
  } else if (!type && hasBillingIssues) {
    isValid = false;
    errorMessage = `Please fill ${billingMissingFields.join(', ')} of Billing address to proceed with E-Way Bill creation`;
  } else if (!type && hasDeliveryIssues) {
    isValid = false;
    errorMessage = `Please fill ${deliveryMissingFields.join(', ')} of Shipping address to proceed with E-Way Bill creation`;
  }

  return {
    isValid,
    errorMessage,
    billingMissingFields,
    deliveryMissingFields,
    hasBillingIssues,
    hasDeliveryIssues,
  };
};

/**
 * Utility function to validate E-way bill addresses from GraphQL response
 * @param {Object} resPartyAddressEntityDetails - GraphQL response containing party address data
 * @returns {Object} - Validation result with isValid flag and error message
 */
export const validateEwaybillFromResponse = (resPartyAddressEntityDetails, type) => {
  const partyBilling = resPartyAddressEntityDetails?.data?.partyBilling?.[0];
  const partyDelivery = resPartyAddressEntityDetails?.data?.partyDelivery?.[0] || [];

  return validateEwaybillAddresses(partyBilling, partyDelivery, type);
};

/**
 * Hook-like function to handle E-way bill validation with callback
 * @param {Object} resPartyAddressEntityDetails - GraphQL response containing party address data
 * @param {Function} showWarningSnackbar - Function to show warning messages
 * @param {Function} setBackdropOpen - Function to control backdrop state
 * @returns {boolean} - Returns true if validation passed, false if validation failed
 */
export const handleEwaybillValidation = (resPartyAddressEntityDetails, showWarningSnackbar, setBackdropOpen, type) => {
  const validation = validateEwaybillFromResponse(resPartyAddressEntityDetails, type);

  if (!validation.isValid) {
    showWarningSnackbar(validation.errorMessage);
    setBackdropOpen(false);
    return false;
  }

  return true;
};

const SPECIAL_EWB_STATE_CODES = new Set([97, 99]);

/**
 * Normalizes a stored / form state value to the NIC numeric code used in e-way bill `pos` options.
 */
export const resolveFormStateValue = (rawStateCode, stateList = [], posList = []) => {
  if (rawStateCode == null || rawStateCode === '' || rawStateCode === 0 || rawStateCode === '0') {
    return '';
  }

  const raw = String(rawStateCode).trim();
  if (!raw) return '';

  const posMatch = posList.find((p) => String(p.value) === raw || Number(p.value) === Number(raw));
  if (posMatch) return String(posMatch.value);

  const byCode = stateList.find((s) => String(s.code) === raw);
  if (byCode?.code != null) return String(byCode.code);

  const byValue = stateList.find((s) => String(s.value) === raw);
  if (byValue?.code != null) return String(byValue.code);

  const normalizedRaw = raw.replace(/District$/i, '').toLowerCase();
  const byLabel = stateList.find((s) => {
    const label = String(s.label || '')
      .replace(/District$/i, '')
      .toLowerCase();
    const value = String(s.value || '')
      .replace(/District$/i, '')
      .toLowerCase();
    return label === normalizedRaw || value === normalizedRaw;
  });
  if (byLabel?.code != null) return String(byLabel.code);

  return raw;
};

/**
 * Resolves NIC e-way bill numeric state code from form state field (pos value, IndiaState code/value/label).
 */
export const toEwbStateCode = (stateField, stateList = [], posList = []) => {
  const resolved = resolveFormStateValue(stateField, stateList, posList);
  if (!resolved) return null;

  const asNum = Number(resolved);
  if (Number.isNaN(asNum)) return null;
  if (SPECIAL_EWB_STATE_CODES.has(asNum)) return asNum;
  if (asNum > 0 && asNum < 100) return asNum;

  return null;
};

/**
 * Derives NIC state code from pincode using master location data (authoritative for NIC pin/state checks).
 */
export const getEwbStateCodeFromPincode = async (pincode, stateList = [], posList = [], locationFetch) => {
  const normalizedPincode = String(pincode ?? '').trim();
  if (!/^\d{6}$/.test(normalizedPincode) || typeof locationFetch !== 'function') {
    return null;
  }

  try {
    const location = await locationFetch(normalizedPincode);
    const stateName = String(location?.State || '')
      .trim()
      .toLowerCase();
    if (!stateName) return null;

    const fromStateList = stateList.find((s) => {
      const label = String(s.label || '')
        .replace(/District$/i, '')
        .trim()
        .toLowerCase();
      return label === stateName;
    });
    if (fromStateList?.code != null) return Number(fromStateList.code);

    const fromPos = posList.find((p) => String(p.label || '').toLowerCase().includes(stateName));
    if (fromPos?.value != null) return Number(fromPos.value);

    return null;
  } catch {
    return null;
  }
};

/**
 * Uses pincode lookup when form state does not match the pincode's state (NIC ship-from / ship-to validation).
 */
export const resolveEwbActStateCode = async (
  pincode,
  formStateField,
  stateList = [],
  posList = [],
  locationFetch
) => {
  const fromForm = toEwbStateCode(formStateField, stateList, posList);
  const fromPincode = await getEwbStateCodeFromPincode(pincode, stateList, posList, locationFetch);

  if (fromPincode != null) {
    if (fromForm != null && fromForm !== fromPincode) {
      return fromPincode;
    }
    return fromPincode;
  }

  return fromForm;
};
